(() => {
  if (customElements.get('vorug-featured-pagination')) return;

  class VorugFeaturedPagination extends HTMLElement {
    connectedCallback() {
      this.frame = requestAnimationFrame(() => this.mount());
    }

    mount() {
      if (!this.isConnected || this.controller) return;
      this.component = this.closest('slider-component');
      this.track = this.component?.querySelector('.slider__grid');
      this.buttons = Array.from(this.querySelectorAll('[data-featured-page]'));
      if (!this.track || this.buttons.length < 2) return;

      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.mobile = matchMedia('(max-width: 768px)');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.page = 0;
      this.track.addEventListener('scroll', () => this.scheduleSync(), { ...options, passive: true });
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-featured-page]');
        if (button && this.contains(button)) this.goTo(Number(button.dataset.featuredPage));
      }, options);
      this.addEventListener('keydown', event => this.onKeydown(event), options);
      this.mobile.addEventListener('change', () => this.scheduleMeasure(), options);
      this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
      this.resizeObserver.observe(this.track);
      this.measure(false);
    }

    scheduleMeasure() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.measure(true));
    }

    measure(preservePage) {
      if (!this.track || !this.isConnected) return;
      const cards = Array.from(this.track.children).filter(card => card.classList.contains('slider__grid-item'));
      this.anchors = cards.filter((card, index) => index % 4 === 0);
      this.hidden = !this.mobile.matches || this.anchors.length < 2;
      if (this.hidden) return;

      const style = getComputedStyle(this.track);
      this.rtl = style.direction === 'rtl';
      const padding = parseFloat(this.rtl ? style.paddingRight : style.paddingLeft) || 0;
      const border = parseFloat(this.rtl ? style.borderRightWidth : style.borderLeftWidth) || 0;
      const rect = this.track.getBoundingClientRect();
      const position = this.track.scrollLeft;
      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      this.offsets = this.anchors.map(card => {
        const cardRect = card.getBoundingClientRect();
        const distance = this.rtl ? rect.right - cardRect.right - padding - border : cardRect.left - rect.left - padding - border;
        const offset = position + (this.rtl ? -distance : distance);
        return this.rtl ? Math.max(-max, Math.min(0, offset)) : Math.max(0, Math.min(max, offset));
      });
      if (preservePage) {
        const page = Math.min(this.page, this.offsets.length - 1);
        this.track.scrollTo({ left: this.offsets[page], behavior: 'instant' });
      }
      this.sync();
    }

    scheduleSync() {
      if (this.syncFrame) return;
      this.syncFrame = requestAnimationFrame(() => {
        this.syncFrame = 0;
        this.sync();
      });
    }

    sync() {
      if (this.hidden || !this.offsets?.length) return;
      const position = this.track.scrollLeft;
      let page = 0;
      this.offsets.forEach((offset, index) => {
        if (Math.abs(position - offset) < Math.abs(position - this.offsets[page])) page = index;
      });
      this.page = page;
      this.buttons.forEach((button, index) => button.setAttribute('aria-current', index === page ? 'true' : 'false'));
    }

    goTo(page) {
      if (this.hidden || !this.offsets?.length) return;
      page = Math.max(0, Math.min(this.offsets.length - 1, page));
      this.component.isOnButtonClick = 0;
      Array.from(this.track.children).forEach(card => card.classList.toggle('is-active', card === this.anchors[page]));
      this.track.scrollTo({ left: this.offsets[page], behavior: this.motion.matches ? 'instant' : 'smooth' });
      this.scheduleSync();
    }

    onKeydown(event) {
      const button = event.target.closest('[data-featured-page]');
      if (!button || !this.contains(button)) return;
      let page = Number(button.dataset.featuredPage);
      if (event.key === 'Home') page = 0;
      else if (event.key === 'End') page = this.buttons.length - 1;
      else if (event.key === 'ArrowRight') page += this.rtl ? -1 : 1;
      else if (event.key === 'ArrowLeft') page += this.rtl ? 1 : -1;
      else return;
      event.preventDefault();
      page = Math.max(0, Math.min(this.buttons.length - 1, page));
      this.buttons[page].focus();
      this.goTo(page);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.syncFrame);
      this.syncFrame = 0;
      this.controller = null;
      this.hidden = true;
    }
  }

  customElements.define('vorug-featured-pagination', VorugFeaturedPagination);
})();
